import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import '@theme/dialog';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #sizePickerAutoSubmit = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    const sizePickerDialog = this.#getSizePickerDialog();
    sizePickerDialog?.addEventListener('dialog:close', this.#handleSizePickerClose, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async (event) => {
    if (event) event.preventDefault();
    this.#recacheTargetAddToCartButton();
    if (!this.#targetAddToCartButton) return;

    // Check if variant is selected
    const productForm = this.#getProductForm();
    const isVariantSelected = productForm ? productForm.getIntendedVariantId() !== undefined : false;

    // If no variant is selected and we are on mobile, show the popup
    if (!isVariantSelected && window.innerWidth < 750) {
      const dialog = this.#getSizePickerDialog();
      if (dialog && typeof dialog.showDialog === 'function') {
        this.#sizePickerAutoSubmit = true;
        const hasOptionsToShow = this.#populateSizePicker(dialog);
        if (hasOptionsToShow) {
          dialog.showDialog();
          return; // Stop the regular add to cart flow
        }
        this.#sizePickerAutoSubmit = false;
      }
    }

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = async (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();

    await this.#syncSizePickerFlow();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const variantPicker = this.#getOriginalVariantPicker();
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  #handleSizePickerClose = () => {
    this.#sizePickerAutoSubmit = false;
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  /**
   * Populates the dialog container with cloned variant option fields.
   * Returns false if there is nothing to show (no picker or no options).
   * @param {import('@theme/dialog').DialogComponent} dialog
   * @returns {boolean}
   */
  #populateSizePicker(dialog) {
    const originalPicker = this.#getOriginalVariantPicker();
    if (!originalPicker) return false;

    const container = dialog.querySelector('.sticky-size-picker-body');
    if (!container) return false;

    const allOptions = this.#getAllOptions(originalPicker);
    if (allOptions.length === 0) return false;

    container.innerHTML = '';
    container.classList.add('variant-picker');
    allOptions.forEach((option) => container.appendChild(this.#cloneOptionForDialog(option)));

    return true;
  }

  /**
   * Clones a variant option element and prepares it for use inside the dialog.
   * @param {HTMLElement} option
   * @returns {HTMLElement}
   */
  #cloneOptionForDialog(option) {
    const clone = /** @type {HTMLElement} */ (option.cloneNode(true));
    this.#remapCloneIds(clone);
    this.#deduplicateRadioNames(clone);
    this.#attachProxyListeners(clone);
    return clone;
  }

  /**
   * Remaps element IDs and their associated label[for] attributes inside a clone
   * to prevent duplicate-ID conflicts with the original picker.
   * @param {HTMLElement} clone
   */
  #remapCloneIds(clone) {
    clone.querySelectorAll('[id]').forEach((el) => {
      const oldId = el.id;
      const newId = `${oldId}-clone-sticky`;
      el.id = newId;
      clone.querySelectorAll(`label[for="${oldId}"]`).forEach((label) => label.setAttribute('for', newId));
    });
  }

  /**
   * Appends "-clone" to radio input names so they form an independent group
   * and don't uncheck selections in the original picker.
   * @param {HTMLElement} clone
   */
  #deduplicateRadioNames(clone) {
    clone.querySelectorAll('input[type="radio"]').forEach((input) => {
      /** @type {HTMLInputElement} */ (input).name += '-clone';
    });
  }

  /**
   * Attaches proxy change listeners to all inputs/selects inside a clone.
   * @param {HTMLElement} clone
   */
  #attachProxyListeners(clone) {
    clone.querySelectorAll('input, select').forEach((input) => {
      input.addEventListener('change', (e) => this.#handleProxySelection(e));
    });
  }

  /**
   * Proxies the change event from the modal clone to the original picker.
   * The variant update listener will re-render the remaining options and submit when complete.
   * @param {Event} event
   */
  #handleProxySelection(event) {
    const target = /** @type {HTMLInputElement | HTMLSelectElement} */ (event.target);
    const value = target.value;
    let name = target.name;
    const originalPicker = this.#getOriginalVariantPicker();
    if (!originalPicker) return;

    // Find the corresponding input in the original picker and change it
    let originalInput;
    if (target.tagName.toLowerCase() === 'input') {
      name = name.replace('-clone', '');
      originalInput = originalPicker.querySelector(`input[name="${name}"][value="${value}"]`);
    } else {
      originalInput = originalPicker.querySelector(`select[name="${name}"]`);
    }

    if (originalInput) {
      if (originalInput.tagName.toLowerCase() === 'input') {
        /** @type {HTMLInputElement} */ (originalInput).checked = true;
      } else {
        /** @type {HTMLSelectElement} */ (originalInput).value = value;
      }
      originalInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Updates the mobile size picker after variant changes.
   */
  async #syncSizePickerFlow() {
    if (!this.#sizePickerAutoSubmit) return;

    const dialog = this.#getSizePickerDialog();
    const dialogElement = dialog?.refs?.dialog;
    if (!dialog || !dialogElement?.open) return;
    const originalPicker = this.#getOriginalVariantPicker();
    if (!originalPicker) return;

    const hasUnselectedOptions = this.#getUnselectedOptions(originalPicker).length > 0;
    this.#populateSizePicker(dialog);
    if (hasUnselectedOptions) return;

    this.#sizePickerAutoSubmit = false;
    await dialog.closeDialog();
    await this.handleAddToCartClick();
  }

  // Helper methods
  /**
   * Gets the sticky size picker dialog component.
   * @returns {import('@theme/dialog').DialogComponent | null}
   */
  #getSizePickerDialog() {
    const dialog = this.querySelector('dialog-component');
    return dialog instanceof HTMLElement ? /** @type {import('@theme/dialog').DialogComponent} */ (dialog) : null;
  }

  /**
   * Gets the current section element.
   * @returns {HTMLElement | null}
   */
  #getSectionElement() {
    return this.closest('.shopify-section');
  }

  /**
   * Gets the original variant picker in the same section.
   * @returns {Element | null}
   */
  #getOriginalVariantPicker() {
    const productId = this.dataset.productId;
    const sectionElement = this.#getSectionElement();
    if (!sectionElement) return null;

    if (productId) {
      const matchingPicker = sectionElement.querySelector(`variant-picker[data-product-id="${productId}"]`);
      if (matchingPicker) return matchingPicker;
    }

    return sectionElement.querySelector('variant-picker');
  }

  /**
   * Gets all variant option groups shown in the size picker.
   * @param {Element} originalPicker
   * @returns {HTMLElement[]}
   */
  #getAllOptions(originalPicker) {
    return Array.from(originalPicker.querySelectorAll('.variant-option')).filter((option) => option instanceof HTMLElement);
  }

  /**
   * Gets the variant options that still require a selection.
   * @param {Element} originalPicker
   * @returns {HTMLElement[]}
   */
  #getUnselectedOptions(originalPicker) {
    return this.#getAllOptions(originalPicker).filter((option) => {
      return !this.#isOptionSelected(option);
    });
  }

  /**
   * Checks whether a variant option already has a valid selection.
   * @param {HTMLElement} option
   * @returns {boolean}
   */
  #isOptionSelected(option) {
    if (option.classList.contains('variant-option--single')) return true;

    const radios = option.querySelectorAll('input[type="radio"]');
    if (radios.length > 0) {
      return Boolean(option.querySelector('input[type="radio"]:checked'));
    }

    const select = option.querySelector('select');
    if (select instanceof HTMLSelectElement) {
      return Boolean(select.value);
    }

    return true;
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.#getSectionElement();
    if (!sectionElement) return null;

    return sectionElement.querySelector(`product-form-component[data-product-id="${productId}"]`);
  }

  /**
   * Re-caches the main product form add-to-cart button.
   */
  #recacheTargetAddToCartButton() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const addToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    if (addToCartButton instanceof HTMLButtonElement) {
      this.#targetAddToCartButton = addToCartButton;
    }
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
